import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API, useAuth, authFetch } from "@/App";
import StarRating from "@/components/StarRating";
import AddToListDialog from "@/components/AddToListDialog";
import FavoriteButton from "@/components/FavoriteButton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Clock, Disc3, Heart, MessageCircle, Send, Check, Music, ListPlus, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export default function AlbumDetail() {
  const { albumId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [userLog, setUserLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [spotifyUrl, setSpotifyUrl] = useState(null);
  const [appleMusicUrl, setAppleMusicUrl] = useState(null);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const [bandcampUrl, setBandcampUrl] = useState(null);

  useEffect(() => {
    fetchData();
  }, [albumId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let albumData = null;
      
      // Check if this is a Deezer album
      if (albumId.startsWith("deezer_")) {
        const deezerId = albumId.replace("deezer_", "");
        const deezerRes = await authFetch(`${API}/deezer/album/${deezerId}`);
        if (deezerRes.ok) {
          albumData = await deezerRes.json();
          // Add default stats for Deezer albums
          albumData.listen_count = albumData.listen_count || 0;
          albumData.avg_rating = albumData.avg_rating || 0;
          albumData.review_count = albumData.review_count || 0;
        }
      } 
      // Check if this is a Discogs album
      else if (albumId.startsWith("discogs_")) {
        const discogsId = albumId.replace("discogs_", "");
        const discogsRes = await authFetch(`${API}/discogs/release/${discogsId}`);
        if (discogsRes.ok) {
          albumData = await discogsRes.json();
          albumData.listen_count = albumData.listen_count || 0;
          albumData.avg_rating = albumData.avg_rating || 0;
          albumData.review_count = albumData.review_count || 0;
        }
      }
      // Otherwise fetch from local mock data
      else {
        const albumRes = await authFetch(`${API}/albums/${albumId}`);
        if (albumRes.ok) {
          albumData = await albumRes.json();
        }
      }

      if (albumData) {
        setAlbum(albumData);
        // Fetch Spotify listen URL
        if (albumData.title && albumData.artist) {
          const encodedTitle = encodeURIComponent(albumData.title);
          const encodedArtist = encodeURIComponent(albumData.artist);
          authFetch(`${API}/spotify/search?title=${encodedTitle}&artist=${encodedArtist}`)
            .then(res => res.ok ? res.json() : null)
            .then(data => { if (data?.spotify_url) setSpotifyUrl(data.spotify_url); })
            .catch(() => {});
          authFetch(`${API}/apple-music/search?title=${encodedTitle}&artist=${encodedArtist}`)
            .then(res => res.ok ? res.json() : null)
            .then(data => { if (data?.apple_music_url) setAppleMusicUrl(data.apple_music_url); })
            .catch(() => {});
          authFetch(`${API}/bandcamp/check?title=${encodedTitle}&artist=${encodedArtist}`)
            .then(res => res.ok ? res.json() : null)
            .then(data => { if (data?.exists && data?.bandcamp_url) setBandcampUrl(data.bandcamp_url); })
            .catch(() => {});
        }
      }

      // Fetch reviews for this album (works for all album types)
      const reviewsRes = await authFetch(`${API}/albums/${albumId}/reviews`);
      if (reviewsRes.ok) {
        const reviewsData = await reviewsRes.json();
        setReviews(reviewsData);
      }

      // Fetch user's logs to check if they've logged this album
      const logsRes = await authFetch(`${API}/logs`);
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        const existingLog = logsData.find((l) => l.album_id === albumId);
        if (existingLog) {
          setUserLog(existingLog);
          setRating(existingLog.rating || 0);
          setReview(existingLog.review || "");
        }
      }
    } catch (error) {
      console.error("Error fetching album:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tags separately (doesn't require auth)
  useEffect(() => {
    authFetch(`${API}/albums/${albumId}/tags`)
      .then(res => res.ok ? res.json() : [])
      .then(setTags)
      .catch(() => {});
  }, [albumId]);

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Log in to add tags"); return; }
    if (!newTag.trim()) return;
    setAddingTag(true);
    try {
      const res = await authFetch(`${API}/albums/${albumId}/tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tag: newTag.trim() }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTags(updated);
        setNewTag("");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add tag");
      }
    } catch { toast.error("Failed to add tag"); }
    finally { setAddingTag(false); }
  };

  const handleRemoveTag = async (tag) => {
    try {
      const res = await authFetch(`${API}/albums/${albumId}/tags/${encodeURIComponent(tag)}`, { method: "DELETE" });
      if (res.ok) setTags(await res.json());
    } catch { toast.error("Failed to remove tag"); }
  };

  const handleLogAlbum = async () => {
    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          album_id: albumId,
          rating: rating || null,
          review: review.trim() || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setUserLog(data);
        toast.success(userLog ? "Updated your log!" : "Album logged!");
        setLogDialogOpen(false);
        fetchData(); // Refresh reviews
      } else {
        toast.error("Failed to log album");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // Get best cover URL
  const getCoverUrl = () => {
    if (!album) return "";
    return album.cover_xl || album.cover_big || album.cover_medium || album.cover_url || album.cover_small || "";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0510] pt-36">
        <div className="animate-pulse">
          <div className="h-96 bg-white/5" />
        </div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen bg-[#0A0510] pt-20 flex items-center justify-center">
        <div className="text-center">
          <Disc3 className="w-12 h-12 text-white/30 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white">Album not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0510]" data-testid="album-detail-page">
      {/* Purple gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/30 via-transparent to-purple-900/20 pointer-events-none" />
      {/* Hero section with blurred background */}
      <div className="relative pt-24 pb-12">
        {/* Blurred album cover background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getCoverUrl()}')`,
            filter: "blur(60px) brightness(0.3)",
            transform: "scale(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-[#0A0510]/60" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Album cover */}
            <div className="flex-shrink-0">
              <img
                src={getCoverUrl()}
                alt={album.title}
                className="w-64 h-64 md:w-80 md:h-80 rounded-xl shadow-2xl object-cover mx-auto md:mx-0"
                data-testid="album-cover"
                onError={(e) => {
                  e.target.src = "https://placehold.co/300x300/1a1a2e/9333ea?text=No+Cover";
                }}
              />
            </div>

            {/* Album info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-2 text-white">
                {album.title}
              </h1>
              <p 
                className="text-xl md:text-2xl text-white/70 mb-4 hover:text-purple-400 cursor-pointer transition-colors inline-block"
                onClick={() => navigate(`/discover?search=${encodeURIComponent(album.artist)}`)}
                title={`View more albums by ${album.artist}`}
                data-testid="artist-link"
              >
                {album.artist}
              </p>

              {/* Metadata */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-6">
                {album.year && (
                  <Badge variant="secondary" className="text-sm bg-purple-500/20 text-purple-300 border-purple-500/30">
                    {album.year}
                  </Badge>
                )}
                {album.genre?.slice(0, 3).map((g) => (
                  <Badge key={g} variant="outline" className="text-sm border-white/20 text-white/70">
                    {g}
                  </Badge>
                ))}
                {album.duration_minutes > 0 && (
                  <span className="flex items-center gap-1 text-sm text-white/50">
                    <Clock className="w-4 h-4" />
                    {album.duration_minutes} min
                  </span>
                )}
                {album.nb_tracks > 0 && (
                  <span className="flex items-center gap-1 text-sm text-white/50">
                    <Music className="w-4 h-4" />
                    {album.nb_tracks} tracks
                  </span>
                )}
              </div>

              {/* External links */}
              <div className="flex items-center gap-4 mb-4">
                {album.link && (
                  <a
                    href={album.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-colors"
                    data-testid="deezer-link"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.81 4.16v3.03H24V4.16h-5.19zM6.27 8.38v3.027h5.19V8.38H6.27zm12.54 0v3.027H24V8.38h-5.19zM6.27 12.594v3.027h5.19v-3.027H6.27zm6.27 0v3.027h5.19v-3.027h-5.19zm6.27 0v3.027H24v-3.027h-5.19zM0 16.81v3.029h5.19v-3.03H0zm6.27 0v3.029h5.19v-3.03H6.27zm6.27 0v3.029h5.19v-3.03h-5.19zm6.27 0v3.029H24v-3.03h-5.19z"/>
                    </svg>
                    Listen on Deezer
                  </a>
                )}
                {spotifyUrl && (
                  <a
                    href={spotifyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-[#1DB954]/10 text-[#1DB954] hover:bg-[#1DB954]/20 transition-colors"
                    data-testid="spotify-link"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                    Listen on Spotify
                  </a>
                )}
                {appleMusicUrl && (
                  <a
                    href={appleMusicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-[#FC3C44]/10 text-[#FC3C44] hover:bg-[#FC3C44]/20 transition-colors"
                    data-testid="apple-music-link"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.802.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.296-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.8-.6-1.965-1.483-.18-.965.46-1.93 1.553-2.16.338-.07.68-.126 1.018-.196.34-.07.504-.26.534-.604V9.654c0-.09-.013-.18-.04-.265-.04-.123-.14-.2-.27-.2a4.825 4.825 0 00-.397.063l-4.603.96c-.026.005-.05.013-.076.02-.18.05-.282.166-.3.35-.005.06-.01.12-.01.18v7.164c0 .478-.063.95-.28 1.388-.294.594-.77.963-1.39 1.135-.324.09-.655.142-.99.16-.988.053-1.848-.588-2.03-1.476-.2-.97.41-1.946 1.495-2.2.33-.076.665-.14.997-.21.376-.082.55-.283.567-.666V8.172c0-.3.04-.593.154-.876.143-.353.4-.585.76-.67.52-.122 1.04-.24 1.56-.353l3.51-.778c.378-.084.758-.163 1.14-.234.278-.052.413.04.474.317.02.09.03.18.03.273v2.263z"/>
                    </svg>
                    Listen on Apple Music
                  </a>
                )}
                {bandcampUrl && (
                  <a
                    href={bandcampUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-[#1DA0C3]/10 text-[#1DA0C3] hover:bg-[#1DA0C3]/20 transition-colors"
                    data-testid="bandcamp-link"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M0 18.75l7.437-13.5H24l-7.438 13.5z"/>
                    </svg>
                    Support the Artist
                  </a>
                )}
              </div>

              {/* Community Tags */}
              <div className="mb-6" data-testid="album-tags">
                <div className="flex flex-wrap items-center gap-2">
                  {tags.map((t) => (
                    <span
                      key={t.tag}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/20"
                    >
                      <Tag className="w-3 h-3" />
                      {t.tag}
                      <span className="text-purple-400/60 ml-0.5">{t.count}</span>
                      {user && t.users?.includes(user.user_id) && (
                        <button onClick={() => handleRemoveTag(t.tag)} className="ml-1 hover:text-red-400 transition-colors">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {user && (
                    <form onSubmit={handleAddTag} className="inline-flex items-center">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="+ add tag"
                        maxLength={30}
                        className="text-xs bg-transparent border border-white/10 rounded-full px-3 py-1 w-24 focus:w-36 transition-all focus:outline-none focus:border-purple-500/50 text-white placeholder:text-white/30"
                        data-testid="add-tag-input"
                        disabled={addingTag}
                      />
                    </form>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center md:justify-start gap-8 mb-8">
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-400">
                    {album.avg_rating > 0 ? album.avg_rating.toFixed(1) : "—"}
                  </p>
                  <p className="text-xs text-white/50">Avg Rating</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{album.listen_count || 0}</p>
                  <p className="text-xs text-white/50">Listens</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{album.review_count || reviews.length}</p>
                  <p className="text-xs text-white/50">Reviews</p>
                </div>
                {album.fans > 0 && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{album.fans.toLocaleString()}</p>
                    <p className="text-xs text-white/50">Fans</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className={`rounded-full px-8 py-6 font-bold ${
                        userLog
                          ? "bg-primary/20 text-primary hover:bg-primary/30"
                          : "bg-primary text-primary-foreground hover:bg-primary/90"
                      }`}
                      data-testid="log-album-btn"
                    >
                      {userLog ? (
                        <>
                          <Check className="w-5 h-5 mr-2" />
                          Logged
                        </>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2" />
                          Log Album
                        </>
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-xl text-white">
                        {userLog ? "Update your log" : "Log this album"}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                      {/* Album preview */}
                      <div className="flex items-center gap-4">
                        <img
                          src={getCoverUrl()}
                          alt={album.title}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div>
                          <p className="font-semibold text-white">{album.title}</p>
                          <p className="text-sm text-white/60">{album.artist}</p>
                        </div>
                      </div>

                      {/* Rating */}
                      <div>
                        <label className="text-sm font-medium mb-2 block text-white">
                          Your Rating
                        </label>
                        <StarRating
                          rating={rating}
                          onRatingChange={setRating}
                          size="large"
                        />
                      </div>

                      {/* Review */}
                      <div>
                        <label className="text-sm font-medium mb-2 block text-white">
                          Review (optional)
                        </label>
                        <Textarea
                          placeholder="Share your thoughts..."
                          value={review}
                          onChange={(e) => setReview(e.target.value)}
                          className="min-h-[120px] bg-white/5 border-white/10 focus:border-purple-500 text-white placeholder:text-white/40"
                          data-testid="review-textarea"
                        />
                      </div>

                      <Button
                        onClick={handleLogAlbum}
                        disabled={submitting}
                        className="w-full rounded-full py-6 font-bold"
                        data-testid="submit-log-btn"
                      >
                        {submitting ? "Saving..." : userLog ? "Update Log" : "Log Album"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Add to List Button */}
                <Button
                  variant="outline"
                  className="rounded-full px-6 py-6 font-bold border-white/20 text-white hover:bg-white/10 hover:border-purple-500"
                  onClick={() => setAddToListOpen(true)}
                  data-testid="add-to-list-btn"
                >
                  <ListPlus className="w-5 h-5 mr-2" />
                  Add to List
                </Button>

                {/* Favorite Button */}
                <FavoriteButton
                  albumId={albumId}
                  size="large"
                  className="!bg-white/10 hover:!bg-pink-500/80"
                />
                
                {/* Add to List Dialog */}
                {album && (
                  <AddToListDialog
                    album={album}
                    open={addToListOpen}
                    onOpenChange={setAddToListOpen}
                  />
                )}

                {userLog && userLog.rating && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/50">Your rating:</span>
                    <StarRating rating={userLog.rating} readonly size="small" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs section */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="reviews" className="w-full">
          <TabsList className="bg-purple-950/50 p-1 rounded-full mb-8">
            <TabsTrigger
              value="reviews"
              className="rounded-full text-white/70 data-[state=active]:bg-purple-500 data-[state=active]:text-white"
            >
              Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger
              value="tracklist"
              className="rounded-full text-white/70 data-[state=active]:bg-purple-500 data-[state=active]:text-white"
            >
              Tracklist ({album.tracks?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="max-w-2xl">
            {reviews.length > 0 ? (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <ReviewCard key={review.log_id} review={review} />
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                <MessageCircle className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">No reviews yet</h3>
                <p className="text-white/50">Be the first to review this album!</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="tracklist" className="max-w-2xl">
            {album.tracks?.length > 0 ? (
              <div className="glass-card rounded-xl overflow-hidden">
                {album.tracks.map((track, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                  >
                    <span className="w-8 text-center text-sm text-white/40">
                      {index + 1}
                    </span>
                    <Music className="w-4 h-4 text-purple-400" />
                    <span className="flex-1 text-white">{track}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                <Music className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">No tracklist available</h3>
                <p className="text-white/50">Track information not available for this album.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ReviewCard({ review }) {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(
    user ? (review.liked_by || []).includes(user.user_id) : false
  );
  const [likeCount, setLikeCount] = useState(review.liked_by?.length || 0);

  const handleLike = async () => {
    if (!user) {
      toast.error("Log in to like reviews");
      return;
    }
    try {
      const response = await authFetch(`${API}/logs/${review.log_id}/like`, {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount(data.like_count);
      } else {
        let errorMsg = "Failed to like review";
        try { const err = await response.json(); errorMsg = err.detail || errorMsg; } catch {}
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Like error:", error);
      toast.error("Failed to like review");
    }
  };

  const timeAgo = review.listened_at
    ? formatDistanceToNow(new Date(review.listened_at), { addSuffix: true })
    : "";

  return (
    <div className="glass-card rounded-xl p-6" data-testid={`review-${review.log_id}`}>
      <div className="flex items-start gap-4">
        <Link to={`/profile/${review.user?.user_id}`}>
          <Avatar className="h-10 w-10 border border-white/10">
            <AvatarImage src={review.user?.picture} alt={review.user?.name} />
            <AvatarFallback className="bg-purple-500/20 text-purple-400">
              {review.user?.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Link
              to={`/profile/${review.user?.user_id}`}
              className="font-semibold text-white hover:text-purple-400 transition-colors"
            >
              {review.user?.name}
            </Link>
            {review.rating && (
              <StarRating rating={review.rating} readonly size="small" />
            )}
          </div>
          <p className="text-xs text-white/50 mb-3">{timeAgo}</p>
          <p className="text-white/80 leading-relaxed">{review.review}</p>

          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/5">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 text-sm transition-colors ${
                isLiked ? "text-purple-400" : "text-white/50 hover:text-white"
              }`}
              data-testid={`review-like-${review.log_id}`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? "fill-purple-400" : ""}`} />
              <span>{likeCount}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
